import {
  createContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Provider as FetchProvider } from 'use-http';
import {
  useLocation, useNavigate, useParams, useLoaderData,
} from 'react-router';
import { init as pendoInit } from '../scripts/pendo';
import { init as totangoInit } from '../scripts/totango';
import { useQuery } from '../common/hooks';
import { LoadingOverlay } from '../common/loadingOverlay/LoadingOverlay.component';
import i18next from '../common/i18next/i18n';
import { useGlobalsConfig } from './app.hooks';

const AppContext = createContext();

const redirectToLogin = () => {
  // instead of a redirect, just reload the page and let the BE handle it
  window.location.reload();
};

const redirectToList = () => {
  // if the csrfToken API loads fine, but the adminProperties and/or urlDetails APIs fail
  // assume that the user is trying to access an event they don't have permissions for
  window.location.href = '/webcast/webcasts';
};

export const AppContextProvider = ({ children }) => {
  const { token: loaderToken = null, adminData: adminPropertiesData = null, globalsConfig: loaderGlobalsConfig = null } = useLoaderData() ?? {};
  const query = useQuery();
  const [adminProperties, setAdminProperties] = useState();
  const [accountProperties, setAccountProperties] = useState();
  const [urls, setUrls] = useState({});
  const [accountPropertiesData, setAccountPropertiesData] = useState();
  const [accountPropertiesLoading, setAccountPropertiesLoading] = useState(true);
  const defaultFetchOptions = useMemo(() => {
    if (loaderToken) {
      window.localStorage.setItem('X-ON24-CSRF-TOKEN', loaderToken);
      return { headers: { 'X-ON24-CSRF-TOKEN': loaderToken } };
    }
    return {};
  }, [loaderToken]);
  const [globalSettingsLoaded, setGlobalSettingsLoaded] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [eventCreationLoading, setEventCreationLoading] = useState(false);
  const headerToken = useRef(loaderToken);
  const { eventId, groupId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const accountPropertiesCall = async (fetchOptions) => {
    setAccountPropertiesLoading(true);
    let queryParams = '';
    if (clientId) queryParams = `clientId=${clientId}`;
    if (eventId && !(pathname.includes('create') && clientId)) queryParams = `eventId=${eventId}`;
    if (groupId) queryParams = `groupId=${groupId}`;
    await fetch(`/webcast/webapi/accountInfo/accountProperties?${queryParams}`,
      fetchOptions, headerToken.current, { ...defaultFetchOptions, cachePolicy: 'no-cache' },
      [clientId, defaultFetchOptions]).then((res) => res.text())
      .then((data) => {
        try {
          const jsonData = JSON.parse(data);
          setAccountPropertiesData(jsonData);
          setClientId(jsonData.clientId);
          setAccountPropertiesLoading(false);
        } catch (e) {
          console.error('accountPropertiesCall', e);
        }
      });
  };

  const urlsCall = async (fetchOptions) => {
    let queryParams = '';
    if (groupId) queryParams = `groupId=${groupId}`;
    if (eventId) queryParams = `&eventId=${eventId}`;
    if (!queryParams) return;
    await fetch(`/webcast/webapi/eventUrl/urlDetails?${queryParams}`, fetchOptions).then((res) => res.text())
      .then((data) => {
        try {
          const jsonData = JSON.parse(data);
          setUrls(jsonData);
        } catch (e) {
          console.error('urlsCall', e);
          redirectToList();
        }
      });
  };

  useEffect(() => {
    if (headerToken.current) {
      setGlobalSettingsLoaded(false);
      accountPropertiesCall(defaultFetchOptions);
    }
  }, [headerToken, defaultFetchOptions, clientId, eventId, groupId]);

  useEffect(() => {
    if ((eventId || groupId) && defaultFetchOptions?.headers) {
      urlsCall({
        ...defaultFetchOptions, cachePolicy: 'no-cache',
      });
    }
  }, [eventId, groupId, defaultFetchOptions]);

  useEffect(() => {
    if (accountPropertiesData && !accountPropertiesLoading && accountPropertiesData.success === undefined) {
      redirectToLogin();
    }
  }, [accountPropertiesData, accountPropertiesLoading]);

  const pendoQuery = query.get('pendo');

  const getProductCd = () => {
    // this disables the active status in the top nav:
    if (window.location.href.includes('/webcast/manageusers')) return null;
    if (window.location.href.includes('/webcast/accountdashboard')) return null;
    if (accountPropertiesData.accountProperties?.isMeetupsSession) return 'meetups';
    return 'webcasts';
  };

  useEffect(() => {
    if (
      adminPropertiesData && accountPropertiesData && !accountPropertiesLoading
    ) {
      setAdminProperties(adminPropertiesData);
      setAccountProperties(accountPropertiesData);
      // TODO stop-gap for accountsettings legacy src code reuse.
      window.isCustomAudienceURLEnabled = accountPropertiesData.accountProperties?.isCustomAudienceURLEnabled;
      window.isRegLobbyTrackingPermitted = accountPropertiesData.adminProperties?.isRegLobbyTrackingPermitted;
      window.productCd = getProductCd();
      window.wccHost = adminPropertiesData.adminProperties?.wccHost;
      window.adminId = adminPropertiesData?.adminId;
      window.clientId = accountPropertiesData.clientId;
      window.isProducerLoginProductPromotion = adminPropertiesData.adminProperties?.isProducerLoginProductPromotion;
      window.isLocalizationEnabled = adminPropertiesData.adminProperties?.isLocalizationEnabled;
      window.isPresenterLogin = adminPropertiesData.adminProperties?.isPresenterLogin;
      window.isEventAdminLogin = adminPropertiesData.adminProperties?.isEventAdminLogin;
      window.eliteLocalizationNginxCache = adminPropertiesData.adminProperties?.eliteLocalizationNginxCache;
      const { adminId = 0, adminEmail = '', isReportOnlyLogin } = adminPropertiesData?.adminProperties;
      const { clientName = '', clientType } = accountPropertiesData?.accountProperties;
      if (isReportOnlyLogin
        && (window.location.href.includes('/webcast/webcasts') || window.location.href.includes('/webcast/sso/webcasts'))) {
        navigate('/webcast/dashboard');
      }
      const {
        pendo: isPendoEnabled = false,
        tontango: isTotangoEnabled = false,
        pendoId = 0,
        totangoId = 0,
      } = adminPropertiesData?.adminProperties;
      if ((pendoQuery === 'true' || isPendoEnabled || clientType === 'WCEP') && pendoId) {
        pendoInit({
          adminId,
          adminEmail,
          clientId: accountPropertiesData.clientId,
          clientName,
          pendoId,
          clientType,
        });
      }
      if (isTotangoEnabled && totangoId) {
        totangoInit({
          adminEmail,
          clientId: accountPropertiesData.clientId,
          clientName,
          totangoId,
        });
      }
      if (!accountPropertiesData.accountProperties.isEliteReactEnabled) {
        navigate(`${window.location.pathname}?clientId=${accountPropertiesData.clientId}`);
      }
    }
    if (!accountPropertiesLoading) {
      setGlobalSettingsLoaded(true);
    }
  }, [adminPropertiesData, accountPropertiesData, accountPropertiesLoading]);

  const { isLoading: isGlobalsConfigLoading, globalsConfig: hookGlobalsConfig } = useGlobalsConfig();
  const globalsConfig = loaderGlobalsConfig ?? hookGlobalsConfig;

  const contextValues = useMemo(() => ({
    globalsConfig,
    defaultFetchOptions,
    t: i18next.t.bind(i18next),
    adminProperties,
    accountProperties,
    globalSettingsLoaded,
    setClientId,
    clientId: clientId === null ? accountPropertiesData?.clientId : clientId,
    urls,
    eventId,
    eventCreationLoading,
    setEventCreationLoading,
  }), [
    globalsConfig,
    defaultFetchOptions,
    i18next,
    adminProperties,
    accountProperties,
    globalSettingsLoaded,
    setClientId,
    clientId,
    urls,
    eventId,
    eventCreationLoading,
    setEventCreationLoading,
  ]);

  if (globalSettingsLoaded && adminProperties && accountProperties) {
    return (
      <FetchProvider
        options={defaultFetchOptions}
      >
        <AppContext.Provider
          value={contextValues}
        >
          {children}
        </AppContext.Provider>
      </FetchProvider>
    );
  }
  return (
    <LoadingOverlay />
  );
};

AppContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default AppContext;
